import { useState, useEffect, useCallback } from 'react';
import type { Podcast, View } from '@/types';
import { searchPodcasts, getTrending, configureApi } from '@/lib/api';
import { PlayerProvider, usePlayer } from '@/context/PlayerContext';
import { FavoritesProvider, useFavorites } from '@/context/FavoritesContext';
import { SearchBar } from '@/components/SearchBar';
import { PodcastGrid } from '@/components/PodcastGrid';
import { PodcastDetail } from '@/components/PodcastDetail';
import { AudioPlayer } from '@/components/AudioPlayer';
import { CategoryList } from '@/components/CategoryList';

function AppContent() {
  const [view, setView] = useState<View>('home');
  const [podcasts, setPodcasts] = useState<Podcast[]>([]);
  const [selectedPodcast, setSelectedPodcast] = useState<Podcast | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>();
  const { favorites } = useFavorites();
  const { state: playerState } = usePlayer();

  // Load trending podcasts on mount
  useEffect(() => {
    loadTrending();
  }, []);

  // Reload trending when category changes
  useEffect(() => {
    if (view === 'home') {
      loadTrending(selectedCategory);
    }
  }, [selectedCategory, view]);

  async function loadTrending(category?: string) {
    setIsLoading(true);
    try {
      const response = await getTrending(20, category);
      if (response.success) {
        setPodcasts(response.feeds);
      }
    } catch (error) {
      console.error('Failed to load trending:', error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleSearch = useCallback(async (query: string) => {
    if (query.length < 2) return;
    setSearchQuery(query);
    setView('search');
    setIsLoading(true);
    try {
      const response = await searchPodcasts(query);
      if (response.success) {
        setPodcasts(response.feeds);
      }
    } catch (error) {
      console.error('Failed to search:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handlePodcastClick = (podcast: Podcast) => {
    setSelectedPodcast(podcast);
    setView('podcast');
  };

  const handleBackToHome = () => {
    setSelectedPodcast(null);
    if (searchQuery) {
      setView('search');
    } else {
      setView('home');
    }
  };

  const handleNavClick = (newView: View) => {
    if (newView === 'home') {
      setSearchQuery('');
      setSelectedPodcast(null);
      loadTrending(selectedCategory);
    } else if (newView === 'favorites') {
      setSelectedPodcast(null);
    }
    setView(newView);
  };

  return (
    <div className={`min-h-screen bg-surface-300 text-white ${playerState.episode ? 'pb-24' : ''}`}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-surface-300/95 backdrop-blur-lg border-b border-white/10">
        <div className="container py-4">
          <div className="flex items-center gap-4 mb-4">
            <h1 className="text-xl font-bold text-white">Podcasts</h1>
            <nav className="flex-1 flex items-center gap-2">
              <button
                onClick={() => handleNavClick('home')}
                className={`nav-tab ${view === 'home' || view === 'search' ? 'active' : ''}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                <span className="hidden sm:inline">Discover</span>
              </button>
              <button
                onClick={() => handleNavClick('favorites')}
                className={`nav-tab ${view === 'favorites' ? 'active' : ''}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
                <span className="hidden sm:inline">Favorites</span>
                {favorites.length > 0 && (
                  <span className="ml-1 text-xs bg-primary/30 px-2 py-0.5 rounded-full">
                    {favorites.length}
                  </span>
                )}
              </button>
            </nav>
          </div>
          {view !== 'podcast' && (
            <SearchBar onSearch={handleSearch} initialValue={searchQuery} />
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-6">
        {view === 'podcast' && selectedPodcast ? (
          <PodcastDetail podcast={selectedPodcast} onBack={handleBackToHome} />
        ) : view === 'favorites' ? (
          <div>
            <h2 className="text-lg font-semibold text-white mb-4">Your Favorites</h2>
            <PodcastGrid
              podcasts={favorites}
              onPodcastClick={handlePodcastClick}
              emptyMessage="No favorites yet. Save podcasts you love!"
            />
          </div>
        ) : (
          <div>
            {view === 'search' && searchQuery ? (
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-white">
                  Results for "{searchQuery}"
                </h2>
                <p className="text-sm text-white/50">
                  {podcasts.length} podcasts found
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-white mb-3">
                    Trending Podcasts
                  </h2>
                  <CategoryList
                    selectedCategory={selectedCategory}
                    onCategorySelect={setSelectedCategory}
                  />
                </div>
              </>
            )}
            <PodcastGrid
              podcasts={podcasts}
              onPodcastClick={handlePodcastClick}
              isLoading={isLoading}
              emptyMessage={
                searchQuery
                  ? 'No podcasts found for your search'
                  : 'No trending podcasts available'
              }
            />
          </div>
        )}
      </main>

      {/* Audio Player */}
      <AudioPlayer />
    </div>
  );
}

interface AppProps {
  apiRoot: string;
  apiNonce: string;
}

export default function App({ apiRoot, apiNonce }: AppProps) {
  // Configure API synchronously before rendering content
  configureApi(apiRoot, apiNonce);

  return (
    <PlayerProvider>
      <FavoritesProvider>
        <AppContent />
      </FavoritesProvider>
    </PlayerProvider>
  );
}
